import { Drawer, Pagination, Table } from '@/components/commons';
import { DeleteContent } from '@/components/commons/DeleteContent';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
  articleStatus,
  Status,
  statusJP,
} from '@/utils';
import {
  ERROR,
  ERROR_MESSAGE_FIELD,
  SUCCESS,
  SUCCESS_MESSAGE,
} from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Input, TableColumnsType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FORM_NAME } from '../Drawers/ArticleForm/constants';
import { formSchemaFn } from '@/pages/Article/Drawers/ArticleForm/formSchemaFn';
import {
  ICreateArticleRequest,
  IUpdateArticleRequest,
} from '@/types/request/article';
import { IUploadImageRequest } from '@/types/request/upload';
import {
  IGetDetailArticleResponse,
  IGetListArticleResponse,
} from '@/types/response/article';
import { ImageService } from '@/services/image';
import { ArticleService } from '@/services';
import { ArticleForm } from '../Drawers/ArticleForm';
import { ITag } from '@/types/response/tag';
import { IEvent, IOffer } from '@/types/response';

interface IProps {
  title?: string;
}

interface IParams {
  limit: number;
  page: number;
  title?: string;
}

export interface ContentImage {
  url?: string;
  file?: File;
  previewUrl?: string;
}

const defaultCover: ContentImage | null = null;

export const ArticleList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailArticleResponse>();
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.ARTICLE.GET_LIST, params],
    () => ArticleService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId)),
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset, setError, watch, setValue } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({ coverImage: defaultCover });
  };

  const handleCreate = async (
    values: ICreateArticleRequest & {
      coverImage?: ContentImage | null;
    },
  ) => {
    const { coverImage, tagNames, offerIds, eventIds, alwaysShowOnTimeline, ...rest } = values as any;
    const payload: ICreateArticleRequest = {
      ...rest,
      alwaysShowOnTimeline: alwaysShowOnTimeline ?? false,
      isCompanyType: rest.isCompanyType ?? false,
      tagNames: (tagNames || [])
        .map((t: any) => String(t).trim())
        .filter((t: string) => t.length),
      offerIds: (offerIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      eventIds: (eventIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      status: values?.status,
    };
    try {
      if (coverImage?.file) {
        const response = await ImageService.uploadFile(coverImage.file);
        payload.coverImageUrl = response?.data?.url;
      }
      await createArticle(payload);
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutate: createArticle, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateArticleRequest) => ArticleService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.CREATE_ARTICLE as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.TAG.GET_LIST]);
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.ARTICLE.GET_DETAIL.replace(':id', String(currentId))],
    () => ArticleService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailArticleResponse>) => {
        const data = response?.data;
        setDetail(data);
        const payload: ICreateArticleRequest = {
          title: data?.title,
          content: data?.content,
          categoryId: data?.category?.id || undefined,
          tagNames: data?.tags?.map((t: ITag) => t.name) || [],
          offerIds:
            data?.offers?.map((e: IOffer) => ({
              value: e.id,
              label: e.title,
            })) || [],
          eventIds:
            data?.events?.map((e: IEvent) => ({
              value: e.id,
              label: e.name,
            })) || [],
          publicAt: moment(data?.publicAt),
          publicEndAt: data?.publicEndAt
            ? moment(data?.publicEndAt)
            : undefined,
          status: data?.status,
          alwaysShowOnTimeline: data?.alwaysShowOnTimeline ?? false,
          isCompanyType: data?.isCompanyType ?? false,
          isDisabled:
            data?.publicEndAt && moment().isAfter(moment(data?.publicEndAt)),
        } as any;
        reset({
          formName: FORM_NAME.UPDATE_ARTICLE,
          ...payload,
          coverImage: data?.imageCover?.url
            ? { url: data?.imageCover?.url, previewUrl: data?.imageCover?.url }
            : null,
          prevStatus: data?.status,
          isDisabled:
            data?.publicEndAt && moment().isAfter(moment(data.publicEndAt)),
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const handleUpdate = async (
    values: IUpdateArticleRequest & {
      coverImage?: ContentImage | null;
    },
  ) => {
    const { coverImage, tagNames, offerIds, eventIds, alwaysShowOnTimeline, ...rest } = values as any;
    const payload: IUpdateArticleRequest = {
      ...rest,
      alwaysShowOnTimeline: alwaysShowOnTimeline ?? false,
      isCompanyType: rest.isCompanyType ?? false,
      tagNames: (tagNames || [])
        .map((t: any) => String(t).trim())
        .filter((t: string) => t.length),
      offerIds: (offerIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      eventIds: (eventIds || [])
        .map((v: any) => Number(v?.value ?? v))
        .filter((v: number) => !isNaN(v)),
      status: values?.status,
      prevStatus: detail?.status,
    };
    delete payload.isDisabled;
    try {
      if (coverImage?.file) {
        const response = await uploadFile(coverImage.file);
        payload.coverImageUrl = response?.data?.url;
      } else if (coverImage?.url) {
        payload.coverImageUrl = coverImage.url;
      }
      await updateArticle({ body: payload, id: Number(currentId) });
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutateAsync: updateArticle, isLoading: isLoadingUpdate } =
    useMutation(
      (payload: { body: IUpdateArticleRequest; id: number }) =>
        ArticleService.update(payload.body, payload.id),
      {
        onSuccess: () => {
          notificationMessage({
            message:
              SUCCESS_MESSAGE[
              SUCCESS.UPDATE_ARTICLE as keyof typeof SUCCESS_MESSAGE
              ],
            type: NOTIFY_STATUS.SUCCESS,
          });
          queryClient.invalidateQueries([ENDPOINT.TAG.GET_LIST]);
          refetch();
          handleCancel();
          setParams({ ...params, page: 1 });
        },
        onError: (error: IResponseError) => {
          handleErrorCommon(error, setError);
          if (error.error === ERROR.ARTICLE_NOT_FOUND) {
            setOpenDrawer(false);
            refetch();
          }
        },
      },
    );

  const { mutate: deleteArticle, isLoading: isLoadingDelete } = useMutation(
    (id: number) => ArticleService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.DELETE_ARTICLE as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError & { code?: string }) => {
        if (
          error.error === ERROR.DELETE_UNSUCCESS ||
          error.code === ERROR.DELETE_UNSUCCESS
        ) {
          notificationMessage({
            message: ERROR_MESSAGE_FIELD.DELETE_UNSUCCESS(
              detail?.title || '記事',
            ),
            type: NOTIFY_STATUS.ERROR,
          });
          return;
        }
        handleErrorCommon(error, setError);

        if (error.error === ERROR.ARTICLE_RELOAD_DATA) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const columns: TableColumnsType<IGetListArticleResponse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      align: 'center',
      width: '5%',
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      width: '50%',
      render: (value, record) => record?.title || '',
    },
    {
      title: '公開日時',
      width: '15%',
      dataIndex: 'publicAt',
      align: 'center',
      render: (value, record) =>
        `${moment(record.publicAt).format('YYYY年MM月DD日 HH:mm')}`,
    },
    {
      title: '公開終了日時',
      width: '15%',
      dataIndex: 'publicEndAt',
      align: 'center',
      render: (value, record) =>
        record?.publicEndAt
          ? `${moment(record.publicEndAt).format('YYYY年MM月DD日 HH:mm')}`
          : '-',
    },
    {
      title: 'ステータス',
      width: '15%',
      dataIndex: 'status',
      align: 'center',
      render: (value, record) => {
        const isPublic = moment(record.publicAt).isSameOrBefore(moment());

        return value === Status.PUBLIC
          ? isPublic
            ? statusJP.PUBLIC
            : statusJP.SCHEDULED
          : statusJP.DRAFT;
      },
    },
  ];

  const onEdit = (record: any) => {
    setCurrentId(record.id);
    setOpenDrawer(true);
  };

  useEffect(() => {
    if (openDrawer) {
      setTimeout(() => {
        const bodies = document.querySelectorAll(
          '.c-drawer .ant-drawer-body',
        ) as NodeListOf<HTMLElement>;
        bodies.forEach((el) => {
          el.scrollTop = 0;
        });
      }, 0);
    }
  }, [openDrawer, currentId, detail]);

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const onSubmit = (values: any) => {
    if (!!currentId) return handleUpdate(values);
    return handleCreate(values);
  };

  const handleDelete = (record: any) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      content: <DeleteContent name='タイトル' content={record?.title} />,
      maskClosable: false,
      onOk: () => deleteArticle(record?.id),
      okText: '削除する',
      cancelText: 'キャンセル',
    });
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px]'>
        <div className='flex justify-between'>
          <div className='flex justify-start gap-[6px] items-center'>
            <Input
              className='w-[300px]'
              prefix={<SearchOutlined className='text-[20px]' />}
              placeholder='タイトル'
              onChange={(e) => {
                setParams({ ...params, page: 1, title: e.target.value });
              }}
            />
          </div>
          <header className='text-right'>
            <Button
              type='primary'
              onClick={() => {
                setOpenDrawer(true);
                reset({
                  formName: FORM_NAME.CREATE_ARTICLE,
                  isCompanyType: false,
                });
                setValue('status', Status.PUBLIC);
              }}
            >
              新規作成
            </Button>
          </header>
        </div>

        <div className='flex justify-between flex-col h-full'>
          <Table
            className='break-all'
            rowKey='id'
            scroll={{ x: 800 }}
            bordered
            loading={isFetching}
            dataSource={data?.data?.data || []}
            columns={columns}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
                setIsNotFoundDetail(false);
              },
            })}
            locale={{
              emptyText: '記事はまだありません',
            }}
          />
          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total || 0}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <FormProvider {...methods}>
        <Drawer
          open={openDrawer}
          title={currentId ? '記事詳細' : '記事新規作成'}
          onCancel={handleCancel}
          loading={
            isLoadingCreate || isLoadingUpdate || isFetchingDetail || isFetching
          }
          isDeleted={!!currentId}
          okText={!currentId ? '保存 ' : '更新 '}
          maskClosable={false}
          onOk={handleSubmit(onSubmit)}
          onDelete={() => handleDelete(detail!)}
        >
          <ArticleForm
            isLoading={
              isLoadingCreate ||
              isLoadingUpdate ||
              isFetchingDetail ||
              isFetching
            }
            article={detail}
          />
        </Drawer>
      </FormProvider>
    </>
  );
};
